(() => {
  const HIDDEN_BOTTOM = "inset(0 0 100% 0)";
  const HIDDEN_TOP = "inset(100% 0 0 0)";
  const SHOWN = "inset(0 0 0 0)";
  const WIPE_MS = 600;
  const WIPE = `clip-path ${WIPE_MS}ms cubic-bezier(0.45, 0.75, 0.1, 0.9)`;

  const HOVER_DELAY = 150;

  const DESKTOP = "(min-width: 990px)";
  const DESKTOP_POINTER = `${DESKTOP} and (hover: hover) and (pointer: fine)`;

  class FeaturedCollections extends HTMLElement {
    connectedCallback() {
      this.layout = this.dataset.layout;
      this.isPreviewCard = this.dataset.animation === "preview_card";
      this.links = Array.from(
        this.querySelectorAll(".featured-collections__link[data-index]")
      );
      this.media = this.isPreviewCard
        ? this.initPreviewTrack()
        : this.initMediaWipe();
      if (!this.media || this.links.length === 0) return;

      if (this.layout === "overlay") {
        this.initOverlay();
      } else {
        this.initSplit();
      }
    }

    disconnectedCallback() {
      if (this._stopLoop) this._stopLoop();
      if (this._stopAutoplay) this._stopAutoplay();
      if (this._stopWipe) this._stopWipe();
      this.clearPendingHover();
    }

    showOnHover(index, apply) {
      this.clearPendingHover();

      if (this.isPreviewCard) {
        apply(index);
        return;
      }

      this._hoverTimer = window.setTimeout(() => {
        this._hoverTimer = null;
        apply(index);
      }, HOVER_DELAY);
    }

    clearPendingHover() {
      if (!this._hoverTimer) return;
      window.clearTimeout(this._hoverTimer);
      this._hoverTimer = null;
    }

    initPreviewTrack() {
      const track = this.querySelector(".featured-collections__preview-track");
      if (!track) return null;

      return {
        show: (index) => track.style.setProperty("--fc-active", index),
      };
    }

    initMediaWipe() {
      const layers = Array.from(
        this.querySelectorAll(
          ".featured-collections__layer:not(.featured-collections__layer--mobile)"
        )
      );
      if (layers.length < 2) return null;

      const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      let current = 0;
      let backdrop = 0;
      let running = false;
      let wipeStart = null;
      let finishTimer = null;

      const setInstant = (layer, value) => {
        layer.style.transition = "none";
        layer.style.clipPath = value;
      };

      const animate = (layer, value) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            layer.style.transition = WIPE;
            layer.style.clipPath = value;
          });
        });
      };

      const finishLater = (done) => {
        window.clearTimeout(finishTimer);
        finishTimer = window.setTimeout(() => {
          finishTimer = null;
          running = false;
          wipeStart = null;
          done();
        }, WIPE_MS + 50);
      };
      this._stopWipe = () => window.clearTimeout(finishTimer);

      const snapTo = (index) => {
        window.clearTimeout(finishTimer);
        finishTimer = null;
        running = false;
        wipeStart = null;
        layers.forEach((layer, i) => {
          setInstant(layer, i === index ? SHOWN : HIDDEN_BOTTOM);
          layer.style.zIndex = i === index ? "2" : "0";
          layer.classList.toggle("is-active", i === index);
        });
        current = index;
        backdrop = index;
      };

      const wipeTo = (index) => {
        if (index === current || !layers[index]) return;
        if (reduced) {
          snapTo(index);
          return;
        }

        if (running && index === backdrop) {
          const retreating = layers[current];
          const target = wipeStart;
          setInstant(retreating, getComputedStyle(retreating).clipPath);
          retreating.classList.remove("is-active");
          layers[index].classList.add("is-active");
          running = true;
          animate(retreating, target);
          finishLater(() => {
            setInstant(retreating, HIDDEN_BOTTOM);
            retreating.style.zIndex = "0";
          });
          current = index;
          return;
        }

        const incoming = layers[index];
        const startClip = index > current ? HIDDEN_BOTTOM : HIDDEN_TOP;
        const cover = running ? backdrop : current;

        layers.forEach((layer, i) => {
          if (i === index) return;
          layer.classList.remove("is-active");
          if (i === current) {
            setInstant(
              layer,
              running ? getComputedStyle(layer).clipPath : SHOWN
            );
            layer.style.zIndex = "1";
          } else if (i === cover) {
            setInstant(layer, SHOWN);
            layer.style.zIndex = "0";
          } else {
            setInstant(layer, HIDDEN_BOTTOM);
            layer.style.zIndex = "0";
          }
        });

        setInstant(incoming, startClip);
        incoming.style.zIndex = "2";
        incoming.classList.add("is-active");
        animate(incoming, SHOWN);

        running = true;
        wipeStart = startClip;
        current = index;
        backdrop = cover;
        finishLater(() => {
          backdrop = index;
        });
      };

      return { show: wipeTo };
    }

    initSplit() {
      const pointerMq = window.matchMedia(DESKTOP_POINTER);

      this.links.forEach((link) => {
        link.addEventListener("mouseenter", () => {
          if (!pointerMq.matches) return;
          this.showOnHover(parseInt(link.dataset.index, 10) || 0, (index) =>
            this.media.show(index)
          );
        });
      });

      const list = this.querySelector(".featured-collections__list");
      if (list) {
        list.addEventListener("mouseleave", () => this.clearPendingHover());
      }

      if (this.isPreviewCard) this.initCursorFollower(pointerMq);
    }

    initCursorFollower(pointerMq) {
      const panel = this.querySelector(".featured-collections__panel");
      const follower = this.querySelector(
        ".featured-collections__preview--cursor"
      );
      const list = this.querySelector(".featured-collections__list");
      if (!panel || !follower || !list) return;

      const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      const OFFSET_X = 24;
      const OFFSET_Y = 24;
      const EASE = 0.16;

      let targetX = 0;
      let targetY = 0;
      let currentX = 0;
      let currentY = 0;
      let rafId = null;
      let active = false;

      const updateTarget = (clientX, clientY) => {
        const rect = panel.getBoundingClientRect();
        const w = follower.offsetWidth;
        const h = follower.offsetHeight;
        const x = clientX - rect.left + OFFSET_X;
        const y = clientY - rect.top + OFFSET_Y;
        targetX = Math.max(0, Math.min(x, rect.width - w));
        targetY = Math.max(0, Math.min(y, rect.height - h));
      };

      const applyTransform = () => {
        follower.style.transform = `translate(${currentX}px, ${currentY}px)`;
      };

      const tick = () => {
        currentX += (targetX - currentX) * EASE;
        currentY += (targetY - currentY) * EASE;
        applyTransform();
        rafId = requestAnimationFrame(tick);
      };

      const stopLoop = () => {
        if (rafId != null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
      };
      this._stopLoop = stopLoop;

      list.addEventListener("mouseenter", (event) => {
        if (!pointerMq.matches) return;
        updateTarget(event.clientX, event.clientY);
        currentX = targetX;
        currentY = targetY;
        applyTransform();
        this.classList.add("is-preview-visible");
        active = true;
        if (!reduced) {
          stopLoop();
          rafId = requestAnimationFrame(tick);
        }
      });

      list.addEventListener("mousemove", (event) => {
        if (!active) return;
        updateTarget(event.clientX, event.clientY);
        if (reduced) {
          currentX = targetX;
          currentY = targetY;
          applyTransform();
        }
      });

      list.addEventListener("mouseleave", () => {
        this.classList.remove("is-preview-visible");
        active = false;
        stopLoop();
      });
    }

    initOverlay() {
      const list = this.querySelector(".featured-collections__list");
      const count = this.links.length;
      let activeIndex = 0;

      const setActive = (index) => {
        activeIndex = ((index % count) + count) % count;
        this.links.forEach((link, i) =>
          link.classList.toggle("is-active", i === activeIndex)
        );
        this.media.show(activeIndex);
      };

      const enabledMq = window.matchMedia(DESKTOP);
      const pointerMq = window.matchMedia(DESKTOP_POINTER);
      const autoplayOn = this.dataset.autoplay === "true";
      const duration = (parseFloat(this.dataset.autoplayDuration) || 5) * 1000;

      let timer = null;
      let paused = false;

      const stop = () => {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      };

      const start = () => {
        stop();
        if (!autoplayOn || paused || count < 2 || !enabledMq.matches) return;
        timer = setInterval(() => setActive(activeIndex + 1), duration);
      };
      this._stopAutoplay = stop;

      this.links.forEach((link) => {
        link.addEventListener("mouseenter", () => {
          if (!pointerMq.matches) return;
          paused = true;
          stop();
          this.showOnHover(parseInt(link.dataset.index, 10) || 0, setActive);
        });
      });

      if (list) {
        list.addEventListener("mouseleave", () => {
          this.clearPendingHover();
          paused = false;
          start();
        });
      }

      document.addEventListener("visibilitychange", () => {
        if (document.hidden) stop();
        else start();
      });

      enabledMq.addEventListener("change", start);

      start();
    }
  }

  if (!customElements.get("featured-collections")) {
    customElements.define("featured-collections", FeaturedCollections);
  }
})();
